/**
 * NotificationBell — header bell icon with unread badge and dropdown inbox.
 *
 * • Polls unread count every 30 s (while the window is focused).
 * • On click, opens a dropdown showing the 50 most recent notifications.
 * • Clicking a notification marks it read and navigates to the relevant entity.
 * • "Mark all read" button at top.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/api';
import { formatExactEntry, formatDateTime } from '../lib/format';

// ── Notification type metadata ────────────────────────────────────────────────
function notifIcon(type) {
  if (type === 'mention_task_comment') return '💬';
  if (type === 'mention_feed_comment') return '🗨️';
  if (type === 'mention_feed_post')    return '📢';
  if (type === 'feed_post')            return '📢';
  if (type === 'task_assigned')        return '📋';
  if (type === 'task_commented')       return '💬';
  if (type === 'task_reassigned')      return '🔄';
  if (type === 'appreciation')         return '🏅';
  if (type === 'appreciation_team')    return '🏅';
  if (type === 'feedback_received')    return '💡';
  if (type === 'task_overdue')         return '⏰';
  if (type === 'review_started')        return '📝';
  if (type === 'review_submitted')      return '📨';
  if (type === 'review_rejected')       return '↩️';
  if (type === 'review_approved')       return '✅';
  if (type === 'performance_review_closed' || type === 'appraisal_closed')     return '🔒';
  if (type === 'performance_review_reopened' || type === 'appraisal_reopened') return '🔓';
  if (type === 'project_join_request')  return '🤝';
  if (type === 'project_join_accepted') return '✅';
  if (type === 'project_join_rejected') return '❌';
  if (type === 'checkin_comment')       return '📝';
  if (type === 'project_commented')     return '💬';
  return '🔔';
}

function notifLabel(type) {
  if (type === 'mention_task_comment') return 'mentioned you in a task comment';
  if (type === 'mention_feed_comment') return 'mentioned you in a feed comment';
  if (type === 'mention_feed_post')    return 'mentioned you in a post';
  if (type === 'feed_post')            return 'posted in the feed';
  if (type === 'task_assigned')        return 'assigned you a task';
  if (type === 'task_commented')       return 'commented on your task';
  if (type === 'task_reassigned')      return 'reassigned a task you were working on';
  if (type === 'appreciation')         return 'appreciated you';
  if (type === 'appreciation_team')    return 'appreciated a teammate';
  if (type === 'feedback_received')    return 'gave you feedback';
  if (type === 'wish')                 return 'sent you a wish';
  if (type === 'task_overdue')         return 'Task is overdue';
  if (type === 'review_started')       return 'started your performance review';
  if (type === 'review_submitted')     return 'submitted a review for your action';
  if (type === 'review_rejected')      return 'returned your performance review';
  if (type === 'review_approved')      return 'approved your performance review';
  if (type === 'performance_review_closed')   return 'closed a performance review';
  if (type === 'performance_review_reopened') return 'reopened a performance review';
  if (type === 'appraisal_closed')            return 'closed an appraisal';
  if (type === 'appraisal_reopened')          return 'reopened an appraisal';
  if (type === 'project_join_request')  return 'wants to join your project';
  if (type === 'project_join_accepted') return 'added you to a project';
  if (type === 'project_join_rejected') return 'declined your project join request';
  if (type === 'checkin_comment')       return 'left you a note on your daily check-in';
  if (type === 'project_commented')     return 'commented on a project';
  return 'sent you a notification';
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function NotificationBell({ appId }) {
  const api            = useApi();
  const navigate       = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();  // guard: don't poll until Clerk is ready

  const [count,    setCount]    = useState(0);
  const [open,     setOpen]     = useState(false);
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const wrapRef                 = useRef(null);
  const prevCount               = useRef(null);
  const audioCtxRef             = useRef(null);

  // Create AudioContext on first user interaction to satisfy browser autoplay policy.
  useEffect(() => {
    const create = () => {
      if (!audioCtxRef.current) {
        try {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (_) {}
      }
    };
    document.addEventListener('click', create, { once: true });
    return () => document.removeEventListener('click', create);
  }, []);

  const playNotifSoundRef = useRef(null);
  playNotifSoundRef.current = () => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state === 'suspended') return;

      const playDing = (freq, when) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, when);
        gain.gain.setValueAtTime(1.0, when);
        gain.gain.exponentialRampToValueAtTime(0.001, when + 0.8);
        osc.start(when);
        osc.stop(when + 0.8);
      };

      playDing(1200, ctx.currentTime);
      playDing(900,  ctx.currentTime + 0.25);
    } catch (_) {}
  };

  // Use a ref so the interval callback always calls the latest version of refreshCount
  // without needing to be in its dependency array (avoids the interval being cleared
  // and reset on every render when api/isSignedIn references change).
  const apiRef        = useRef(api);
  const isSignedInRef = useRef(isSignedIn);
  const isLoadedRef   = useRef(isLoaded);
  const appIdRef      = useRef(appId);
  apiRef.current        = api;
  isSignedInRef.current = isSignedIn;
  isLoadedRef.current   = isLoaded;
  appIdRef.current      = appId;

  const refreshCount = useCallback(async () => {
    if (!isLoadedRef.current || !isSignedInRef.current) return;
    try {
      const r    = await apiRef.current.notifications.unreadCount(appIdRef.current);
      const next = r.count || 0;
      if (prevCount.current !== null && next > prevCount.current) {
        playNotifSoundRef.current?.();
      }
      prevCount.current = next;
      setCount(next);
    } catch (_) { /* ignore — backend already returns 0 on error */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up polling once — stable interval that won't be torn down on re-renders
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30_000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // ── Load full list when opened ─────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.notifications.list(appId ? { app_id: appId } : undefined);
      setItems(r.items || []);
    } catch (_) { /* ignore */ } finally {
      setLoading(false);
    }
  }, [api, appId]);

  const toggle = () => {
    if (!open) loadItems();
    setOpen(v => !v);
  };

  // ── Close on outside click ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Mark one read + navigate ───────────────────────────────────────────────
  const handleClick = async (item) => {
    if (!item.read_at) {
      try { await api.notifications.markRead(item.id); } catch (_) {}
      setItems(prev => prev.map(n => n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n));
      setCount(c => Math.max(0, c - 1));
    }
    setOpen(false);

    // Navigate to the relevant page
    const taskTypes = ['mention_task_comment', 'task_assigned', 'task_commented', 'task_reassigned', 'task_overdue'];
    const feedTypes = ['mention_feed_comment', 'mention_feed_post', 'feed_post', 'appreciation', 'appreciation_team', 'feedback_received', 'wish'];
    const perfTypes = ['review_started', 'review_rejected', 'review_approved',
      'performance_review_closed', 'performance_review_reopened', 'appraisal_closed', 'appraisal_reopened'];
    const projectTypes = ['project_join_request', 'project_join_accepted', 'project_join_rejected', 'project_commented'];
    if (taskTypes.includes(item.type) && item.task_id) {
      navigate(`/apps/${item.app_id}/tasks/${item.task_id}`);
    } else if (feedTypes.includes(item.type)) {
      if (item.type === 'feedback_received') {
        navigate(`/apps/${item.app_id}/feed?tab=Feedback`);
      } else if (item.type === 'appreciation') {
        navigate(`/apps/${item.app_id}/feed?tab=Appreciations`);
      } else if (item.type === 'appreciation_team') {
        // Someone else's appreciation, broadcast to the rest of the team —
        // the Appreciations tab only shows the clicking user's own
        // given/received appreciations, so this one would never appear
        // there. It's visible in the general Feed's "All Activity" stream
        // (the public feed post created alongside it), so land there instead.
        navigate(`/apps/${item.app_id}/feed`);
      } else {
        navigate(`/apps/${item.app_id}/feed`);
      }
    } else if (perfTypes.includes(item.type)) {
      navigate(`/apps/${item.app_id}/performance`);
    } else if (projectTypes.includes(item.type)) {
      navigate(`/apps/${item.app_id}/projects`);
    }
  };

  // ── Mark all read ──────────────────────────────────────────────────────────
  const handleMarkAll = async () => {
    try {
      await api.notifications.markAllRead(appId);
      setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setCount(0);
    } catch (_) {}
  };

  const unread = items.filter(n => !n.read_at).length;

  const handleAcceptJoin = async (e, n) => {
    e.stopPropagation();
    try {
      await api.acceptProjectJoin(n.app_id, n.entity_id, n.task_id);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, join_request_status: 'accepted', read_at: x.read_at || new Date().toISOString() } : x));
      setCount(c => n.read_at ? c : Math.max(0, c - 1));
      try { await api.notifications.markRead(n.id); } catch (_) {}
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept join request');
    }
  };

  const handleRejectJoin = async (e, n) => {
    e.stopPropagation();
    try {
      await api.rejectProjectJoin(n.app_id, n.entity_id, n.task_id);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, join_request_status: 'rejected', read_at: x.read_at || new Date().toISOString() } : x));
      setCount(c => n.read_at ? c : Math.max(0, c - 1));
      try { await api.notifications.markRead(n.id); } catch (_) {}
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject join request');
    }
  };

  return (
    <div ref={wrapRef} className="notif-bell-wrap">
      {/* Bell button */}
      <button
        className={`notif-bell-btn${count > 0 ? ' has-unread' : ''}`}
        onClick={toggle}
        title="Notifications"
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
      >
        <span className="notif-bell-icon">🔔</span>
        {count > 0 && (
          <span className="notif-badge">{count > 99 ? '99+' : count}</span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="notif-dropdown">
          {/* Header */}
          <div className="notif-dropdown-head">
            <span className="notif-dropdown-title">Notifications</span>
            {unread > 0 && (
              <button className="notif-mark-all-btn" onClick={handleMarkAll}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="notif-list">
            {loading && (
              <div className="notif-empty">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="notif-empty">
                <span style={{ fontSize: 28 }}>🔔</span>
                <span>No notifications yet</span>
              </div>
            )}
            {!loading && items.map((n) => {
              const actorName = [n.actor_first_name, n.actor_last_name].filter(Boolean).join(' ')
                || (n.actor_email ? n.actor_email.split('@')[0] : 'Someone');
              const isUnread  = !n.read_at;
              // Some notification types (e.g. appreciation) mean different things to
              // different recipients — "X appreciated you" vs "X appreciated Y" for
              // everyone else. The server already computes the right one per-recipient
              // in n.title; using notifLabel() unconditionally would show the same
              // generic text to everyone regardless of who was actually involved.
              const titleText = n.title || `${actorName} ${notifLabel(n.type)}`;

              return (
                <div
                  key={n.id}
                  className={`notif-item${isUnread ? ' unread' : ''}`}
                  onClick={() => handleClick(n)}
                >
                  {/* Left: icon */}
                  <div className="notif-item-icon">{notifIcon(n.type)}</div>

                  {/* Middle: content */}
                  <div className="notif-item-body">
                    <div className="notif-item-title">
                      {n.type === 'task_overdue' ? (
                        <strong>{notifLabel(n.type)}</strong>
                      ) : titleText.startsWith(actorName) ? (
                        <><strong>{actorName}</strong>{titleText.slice(actorName.length)}</>
                      ) : (
                        titleText
                      )}
                      {n.app_name && (
                        <span className="notif-app-badge"> · {n.app_name}</span>
                      )}
                    </div>
                    {n.preview && (
                      <div className="notif-item-preview">{n.preview}</div>
                    )}
                    <div className="notif-item-time" title={formatDateTime(n.created_at)}>
                      {formatExactEntry(n.created_at)}
                    </div>
                    {n.type === 'project_join_request' && n.task_id && (
                      <div style={{ marginTop: 6 }}>
                        {n.join_request_status === 'accepted' ? (
                          <span style={{ fontSize: 12, color: '#388e3c' }}>✓ Accepted</span>
                        ) : n.join_request_status === 'rejected' ? (
                          <span style={{ fontSize: 12, color: '#c62828' }}>✗ Declined</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn-primary"
                              style={{ fontSize: 12, padding: '3px 10px' }}
                              onClick={(e) => handleAcceptJoin(e, n)}
                            >
                              Accept
                            </button>
                            <button
                              className="btn-secondary"
                              style={{ fontSize: 12, padding: '3px 10px' }}
                              onClick={(e) => handleRejectJoin(e, n)}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: unread dot */}
                  {isUnread && <div className="notif-unread-dot" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
